import { createSelector } from "@reduxjs/toolkit";
import {
  selectSpecifiedArticlesValidationColorItems,
  selectStandardRoomValidationColorItems,
  selectValidationDecisionColorItems,
} from "../../Room/drofusOccurrenceValidationColoringSelectors";
import { IRootState } from "../../app/store";
import { ROOM_COLORING_IDS } from "../../RoomColoring";
import { sortArrayByStrings } from "../../globalUtils";
import { selectHttpSingleRoomFull } from "../../Room/httpRoomSelectors";
import {
  DROFUS_OCCURRENCE_VALIDATION_OVERALL_ID,
  makeDrofusOccurrenceValidationListTypeColoringId,
  makeDrofusOccurrenceValidationResponsibilityColoringId,
  parseDrofusOccurrenceValidationColoringId,
  titleForDrofusOccurrenceValidationColoringId,
} from "../../DrofusOccurrenceValidation/drofusOccurrenceValidationIds";

export type IDrofusOccurrenceValidationBarRowItem = {
  discipline: string; // "E" | "V" | ...
  validationId?: string; // "standardRoom.E"
  decisionId?: string | ROOM_COLORING_IDS; // "decision:standardRoom.E"
};

export type IDrofusOccurrenceValidationBarSpecifiedRowItem = {
  id: string;
  title: string;
  kind: "overall" | "listType" | "responsibility";
  value: string | null;
};

const getDisciplineFromStandardRoomKey = (key: string): string | null => {
  const idx = key.indexOf(".");
  if (idx === -1 || idx + 1 >= key.length) return null;
  return key.slice(idx + 1);
};

const getDisciplineFromDecisionFilterId = (id: string): string | null => {
  const prefixIdx = id.indexOf(":");
  const withoutPrefix = prefixIdx >= 0 ? id.slice(prefixIdx + 1) : id;
  const idx = withoutPrefix.indexOf(".");
  if (idx === -1 || idx + 1 >= withoutPrefix.length) return null;
  return withoutPrefix.slice(idx + 1);
};

/**
 * Standardrom (poeng) + beslutning per disiplin, ferdig joinet og sortert.
 */
export const selectDrofusOccurrenceValidationStandardRoomRows = createSelector(
  [selectStandardRoomValidationColorItems, selectValidationDecisionColorItems],
  (standardRoomValidations, decisionFilters): IDrofusOccurrenceValidationBarRowItem[] => {
    const map = new Map<string, IDrofusOccurrenceValidationBarRowItem>();

    for (const v of standardRoomValidations) {
      const discipline = getDisciplineFromStandardRoomKey(v.id);
      if (!discipline) continue;
      const existing = map.get(discipline) ?? { discipline };
      existing.validationId = v.id;
      map.set(discipline, existing);
    }

    for (const d of decisionFilters) {
      const discipline = getDisciplineFromDecisionFilterId(d.id);
      if (!discipline) continue;
      const existing = map.get(discipline) ?? { discipline };
      existing.decisionId = d.id;
      map.set(discipline, existing);
    }

    return Array.from(map.values()).sort((a, b) => a.discipline.localeCompare(b.discipline));
  }
);

/**
 * SpecifiedArticles dropdown-items (id + title).
 */
export const selectDrofusOccurrenceValidationSpecifiedRows = createSelector(
  [selectSpecifiedArticlesValidationColorItems],
  (specifiedArticles): IDrofusOccurrenceValidationBarSpecifiedRowItem[] => {
    return (specifiedArticles ?? []).map((i) => {
      const scope = parseDrofusOccurrenceValidationColoringId(i.id);
      return {
        id: i.id,
        title: i.title,
        kind: scope?.type ?? "overall",
        value: scope && scope.type !== "overall" ? scope.value : null,
      };
    });
  }
);

export const selectDrofusOccurrenceValidationListTypeRows = createSelector(
  [selectDrofusOccurrenceValidationSpecifiedRows],
  (rows) => rows.filter((row) => row.kind === "overall" || row.kind === "listType")
);

export const selectDrofusOccurrenceValidationResponsibilityRows = createSelector(
  [selectDrofusOccurrenceValidationSpecifiedRows],
  (rows) => rows.filter((row) => row.kind === "responsibility")
);

export const selectActiveRoomColoringId = (state: IRootState) => state.design.activeRoomColoringId;

export const selectActiveSpecifiedItem = createSelector(
  [selectDrofusOccurrenceValidationSpecifiedRows, selectActiveRoomColoringId],
  (specifiedRows, activeRoomColoringId) => {
    return specifiedRows.find((r) => r.id === activeRoomColoringId) ?? null;
  }
);

export const selectSpecifiedArticlesValidation = createSelector(
  [selectHttpSingleRoomFull],
  (room) => room?.validation?.specifiedArticles ?? null
);

export const selectSpecifiedArticlesResponsibilities = createSelector(
  [selectSpecifiedArticlesValidation],
  (specified): string[] => {
    const keys = Object.keys(specified?.perResponsibility ?? {});
    keys.sort((a, b) => a.localeCompare(b));
    return keys;
  }
);

export const selectSpecifiedArticlesListTypes = createSelector(
  [selectSpecifiedArticlesValidation],
  (specified): string[] => {
    const keys = Object.keys(specified?.perListType ?? {});
    keys.sort((a, b) => a.localeCompare(b));
    return keys;
  }
);

export const selectDrofusOccurrenceValidationListType = (state: IRootState) =>
  state.drofusOccurrenceValidation.specifiedArticlesListType;

export const selectDrofusOccurrenceValidationResponsibility = (state: IRootState) =>
  state.drofusOccurrenceValidation.specifiedArticlesResponsibility;

export const selectDrofusOccurrenceValidationFilterMode = (state: IRootState) =>
  state.drofusOccurrenceValidation.filterMode;

export const selectSpecifiedArticlesActiveSnapshot = createSelector(
  [
    selectSpecifiedArticlesValidation,
    selectDrofusOccurrenceValidationListType,
    selectDrofusOccurrenceValidationResponsibility,
  ],
  (specified, activeListType, activeResp) => {
    if (!specified) return null;

    if (activeListType && specified.perListType?.[activeListType]) {
      return specified.perListType[activeListType];
    }

    if (activeResp && specified.perResponsibility?.[activeResp]) {
      return specified.perResponsibility[activeResp];
    }

    return specified; // top-level snapshot (samlet)
  }
);

export const selectSpecifiedArticlesDiffItems = createSelector(
  [selectSpecifiedArticlesActiveSnapshot],
  (snapshot) => sortArrayByStrings(snapshot?.comparison ?? [], ["compCode"])
);

export const selectSpecifiedArticlesMatchRatePct = createSelector(
  [selectSpecifiedArticlesActiveSnapshot],
  (snapshot) => snapshot?.summary?.matchRatePct ?? null
);

export const selectSpecifiedArticlesValidatedAt = createSelector(
  [selectSpecifiedArticlesValidation],
  (specified) => specified?.validatedAt ?? null
);

export const selectShouldShowSpecifiedArticlesSegmented = createSelector(
  [selectSpecifiedArticlesResponsibilities],
  (responsibilities) => responsibilities.length > 0
);

export const selectDrofusOccurrenceValidationActiveListTypeColoringId = createSelector(
  [selectDrofusOccurrenceValidationListType],
  (listType) =>
    listType
      ? makeDrofusOccurrenceValidationListTypeColoringId(listType)
      : DROFUS_OCCURRENCE_VALIDATION_OVERALL_ID
);

export const selectDrofusOccurrenceValidationActiveResponsibilityColoringId = createSelector(
  [selectDrofusOccurrenceValidationResponsibility],
  (responsibility) =>
    responsibility ? makeDrofusOccurrenceValidationResponsibilityColoringId(responsibility) : null
);

export const getDrofusOccurrenceValidationStateFromColoringId = (id: string | undefined) => {
  const scope = parseDrofusOccurrenceValidationColoringId(id);
  return {
    listType: scope?.type === "listType" ? scope.value : null,
    responsibility: scope?.type === "responsibility" ? scope.value : null,
  };
};

export {
  DROFUS_OCCURRENCE_VALIDATION_OVERALL_ID,
  makeDrofusOccurrenceValidationListTypeColoringId,
  makeDrofusOccurrenceValidationResponsibilityColoringId,
  titleForDrofusOccurrenceValidationColoringId,
};

export const selectValidationToolbarMode = createSelector(
  [selectActiveRoomColoringId, selectDrofusOccurrenceValidationSpecifiedRows, selectDrofusOccurrenceValidationStandardRoomRows],
  (colorId, occurrenceRows, standardRows) => ({
    mode: colorId?.includes("standardRoom.") ? "standard" as const : "occurrence" as const,
    occurrenceId: occurrenceRows.find(row => row.kind === "overall")?.id ?? occurrenceRows[0]?.id,
    standardId: standardRows.find(row => row.validationId)?.validationId
      ?? standardRows.find(row => row.decisionId)?.decisionId,
  }),
);
